import React, { useLayoutEffect, useState } from 'react';
import { FaUser } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import validator from 'email-validator';
import { toast } from 'react-toastify';
import { BusinessInvite, CustomFieldType } from '../../../services/model/inviteService.model';
import { LocalContactInfo } from '../../../services/model/localContactService.model';
import {
  createEmptyBusinessInvite,
  createEmptyField,
  createEmptyLocalContact,
} from '../../../utils/formatUtils';
import localContactService from '../../../services/localContactService';
import inviteService from '../../../services/inviteService';
import ModalLayout from '../layouts/ModalLayout';
import NameInput from '../forms/profile/NameInput';
import { sortProfileFields } from '../../../utils/sortUtils';
import ProfileFieldForm from '../forms/ProfileFieldForm';
import CustomFieldsForm from '../forms/CustomFieldsForm';
import CommunicationNameInput from '../forms/profile/CommunicationNameInput';
import { isEmptyField, isValidLocalContact } from '../../../utils/validationUtils';
import CancelSaveButton from '../misc/CancelSaveButton';
import ImageInput from '../forms/ImageInput';
import IconBadge from '../icons/IconBadge';
import { isMoments } from '../../../constants';
import { ProfileDataType, ProfileField } from '../../../types/Profile';

interface LocalContactModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  connectionId?: string;
}

export default function LocalContactModal({ open, setOpen, connectionId }: LocalContactModalProps) {
  const { t } = useTranslation();

  const [invite, setInvite] = useState<BusinessInvite>(createEmptyBusinessInvite());
  const [localContact, setLocalContact] = useState<LocalContactInfo>(createEmptyLocalContact());
  const [image, setImage] = React.useState<string>();
  const [imageAction, setImageAction] = React.useState<'UPLOAD' | 'DELETE'>();
  const [validate, setValidate] = useState(false);
  const [sentInvite, setSentInvite] = useState(false);

  useLayoutEffect(() => {
    const fetchConnection = async () => {
      if (!open) return;
      const fetchedInvite = await inviteService.fetchInvite();

      if (connectionId) {
        const [contactInfo, profilePicture] = await Promise.all([
          localContactService.fetchLocalContact(connectionId).catch(() => undefined),
          localContactService.getProfilePictureAsFile(connectionId).catch(() => undefined),
          inviteService.fetchInvite(),
        ]);
        setInvite(fetchedInvite);

        if (contactInfo)
          setLocalContact({
            ...contactInfo,
            receivedData: [...fetchedInvite.mandatoryFields, ...fetchedInvite.optionalFields].map(
              (f) =>
                contactInfo.receivedData.find(
                  (d) => d.dataType === f || d.social?.socialType === f,
                ) || createEmptyField(f),
            ),
          });
        if (profilePicture) setImage(window.URL.createObjectURL(profilePicture));
        else setImage(undefined);
      } else {
        setInvite(fetchedInvite);
        setImage(undefined);
        setValidate(false);
        setImageAction('UPLOAD');
        setLocalContact({
          ...createEmptyLocalContact(),
          receivedData: [
            ...(fetchedInvite.mandatoryFields || []),
            ...(fetchedInvite.optionalFields || []),
          ].map(createEmptyField),
        });
      }
    };

    fetchConnection();
  }, [connectionId, open]);

  const handleSave = async () => {
    setValidate(true);
    if (!isValidLocalContact(localContact, invite)) return;
    let contactId = connectionId;
    if (connectionId) {
      await localContactService.updateLocalContact(
        connectionId,
        localContact.firstName,
        localContact.lastName,
        localContact.note,
        localContact.receivedData,
        localContact.customFieldResponses,
      );
    } else {
      const { id } = await localContactService
        .createLocalContacts([
          {
            ...localContact,
            receivedData: localContact.receivedData.filter((f) => !isEmptyField(f)),
          },
        ])
        .then((res) => res?.[0]);
      contactId = id;
    }

    if (sentInvite && contactId){
      localContactService.inviteContacts([contactId]).then(() => toast.success(t('toast.success.inviteSent_one')));
    }

    if (imageAction === 'UPLOAD' && image)
      await localContactService.uploadProfilePicture(
        contactId!,
        await fetch(image!).then((res) => res.blob() as Promise<File>),
      );
    else if (imageAction === 'DELETE' && connectionId)
      await localContactService.deleteProfilePicture(contactId!);

    setOpen(false);
  };

  const communicationName = invite.customFields.find(
    (f) => f.type === CustomFieldType.COMMUNICATION_NAME,
  );

  const handleFieldChangeByType = (newField: ProfileField) => {
    const receivedData =
      newField.dataType === ProfileDataType.SOCIAL
        ? localContact.receivedData.map((oldField) =>
            oldField.social?.socialType === newField.social?.socialType ? newField : oldField,
          )
        : localContact.receivedData.map((oldField) =>
            oldField.dataType === newField.dataType ? newField : oldField,
          );
    setLocalContact({ ...localContact, receivedData });
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 my-8 w-full rounded-[20px] bg-secondary-50 p-8 sm:max-w-[700px]">
      <div className="flex border-b border-secondary-200">
        <IconBadge icon={FaUser} />

        <div className="mb-4 ml-4 flex w-full flex-col">
          <h2 className="text-2xl">
            {connectionId
              ? t('component.modal.localContact.edit.title')
              : t('component.modal.localContact.create.title')}
          </h2>
          <p>
            {connectionId
              ? t('component.modal.localContact.edit.subtitle')
              : t('component.modal.localContact.create.subtitle')}
          </p>
        </div>
      </div>
      <div className="mt-4 flex w-full flex-col gap-4">
        <ImageInput
          className="h-32 w-32"
          iconClassName="w-14 h-14"
          image={image}
          setImage={setImage}
          setImageAction={setImageAction}
        />

        <NameInput
          alias={`${localContact.firstName || ''}+${localContact.lastName || ''}`}
          validate={validate}
          setAlias={(s) => {
            const [firstName, lastName] = s.split('+');
            setLocalContact({ ...localContact, firstName, lastName });
          }}
        />

        {communicationName && isMoments && (
          <CommunicationNameInput
            name={localContact.customFieldResponses[communicationName?.customFieldId!] || ''}
            setName={(newField) =>
              setLocalContact({
                ...localContact,
                customFieldResponses: {
                  ...localContact.customFieldResponses,
                  [communicationName?.customFieldId!]: newField,
                },
              })
            }
          />
        )}
        {sortProfileFields(localContact.receivedData).map((f, index) => {
          const isEmail =
            f.dataType === ProfileDataType.EMAIL && f.email && validator.validate(f.email);

          return (
            <React.Fragment key={index}>
              <ProfileFieldForm
                field={f}
                canBeEmpty
                validate={validate}
                setField={handleFieldChangeByType}
              />
              {isEmail && (
                <div className="flex items-center gap-2 pl-1 mt-1">
                  <input
                    type="checkbox"
                    id="useMainEmail"
                    checked={sentInvite}
                    onChange={(e) => setSentInvite(e.target.checked)}
                    className="accent-primary-500"
                  />
                  <label htmlFor="useMainEmail" className="text-sm">
                    {t('component.modal.localContact.sendInvite')}
                  </label>
                </div>
              )}
            </React.Fragment>
          );
        })}

        <CustomFieldsForm
          fields={invite.customFields || []}
          response={localContact.customFieldResponses}
          setResponse={(newResponse) =>
            setLocalContact({ ...localContact, customFieldResponses: newResponse })
          }
        />

        {/* Description */}
        <div className="flex flex-col gap-2">
          <p className="mb-2 text-lg font-medium">{t('component.modal.localContact.noteTitle')}</p>
          <textarea
            className="mb-2 h-32 w-full resize-none rounded-lg placeholder-secondary-200"
            placeholder="Schrijf hier je opmerkingen..."
            onChange={(e) => setLocalContact({ ...localContact, note: e.target.value })}
            value={localContact.note}
          />
        </div>

        {/* Triggers rerender of table when assigning so can only be added once we deal with rerender issue */}
        {/* {connection && <TagDisplay connection={connection} withHover={!isMobile} />} */}
      </div>
      <div className="flex w-full flex-1 flex-col items-end gap-2">
        <p
          className={classNames('w-fit justify-end text-sm text-danger', {
            invisible: !validate || isValidLocalContact(localContact, invite),
          })}>
          {t('component.modal.localContact.invalid')}
        </p>

        <CancelSaveButton onCancel={() => setOpen(false)} onSave={handleSave} />
      </div>
    </ModalLayout>
  );
}
